import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { NotificationsService } from "../../services";
import { dateTime, todayISO } from "../../utils/format";
import {
  Filter,
  RefreshCw,
  Search,
} from "lucide-react";

const todayString = () => todayISO();

const initialFilters = () => ({
  fromDate: todayString(),
  toDate: todayString(),
  notificationType: "ALL",
  channel: "ALL",
  status: "ALL",
  search: "",
});

const normalizeRows = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  return response?.data ?? [];
};

export const NotificationLogsPage = () => {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(true);

  const loadLogs = useCallback(async () => {
    setLoading(true);

    try {
      const response = await NotificationsService.logs(filters);
      setRows(normalizeRows(response));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const delay =
      filters.search && filters.search.trim().length > 0
        ? 350
        : 0;

    const timer = window.setTimeout(() => {
      loadLogs();
    }, delay);

    return () => window.clearTimeout(timer);
  }, [loadLogs, filters.search]);

  const totalLogs = rows.length;

  const successCount = useMemo(
    () =>
      rows.filter(
        (row) => row.status === "SUCCESS" || row.status === "SENT"
      ).length,
    [rows]
  );

  const failedCount = useMemo(
    () => rows.filter((row) => row.status === "FAILED").length,
    [rows]
  );

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div>
      <PageHeader
        title="Notification Logs"
        subtitle="Email, WhatsApp and SMS notification history with success or failure status."
        actions={
          <button
            type="button"
            onClick={loadLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw
              className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        }
      />

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Total Logs
          </p>
          <p className="mt-2 font-display text-2xl font-semibold text-slate-950">
            {loading ? "…" : totalLogs}
          </p>
        </div>

        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Success
          </p>
          <p className="mt-2 font-display text-2xl font-semibold text-slate-950">
            {loading ? "…" : successCount}
          </p>
        </div>

        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Failed
          </p>
          <p className="mt-2 font-display text-2xl font-semibold text-slate-950">
            {loading ? "…" : failedCount}
          </p>
        </div>
      </div>

      <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <h3 className="font-display text-base font-semibold text-slate-900">
            Filters
          </h3>
          <span className="text-xs text-slate-400">
            Auto updates when a filter changes
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[145px_145px_190px_150px_150px_1fr]">
          <input
            type="date"
            value={filters.fromDate}
            onChange={(event) =>
              updateFilter("fromDate", event.target.value)
            }
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
          />

          <input
            type="date"
            value={filters.toDate}
            onChange={(event) =>
              updateFilter("toDate", event.target.value)
            }
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
          />

          <select
            value={filters.notificationType}
            onChange={(event) =>
              updateFilter("notificationType", event.target.value)
            }
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
          >
            <option value="ALL">All Types</option>
            <option value="DAILY_REPORT">Daily Report</option>
            <option value="LOW_STOCK">Low Stock</option>
            <option value="CASHIER_REMINDER">Cashier Reminder</option>
            <option value="CASH_SETTLEMENT">Cash Settlement</option>
          </select>

          <select
            value={filters.channel}
            onChange={(event) =>
              updateFilter("channel", event.target.value)
            }
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
          >
            <option value="ALL">All Channels</option>
            <option value="EMAIL">Email</option>
            <option value="WHATSAPP">WhatsApp</option>
            <option value="SMS">SMS</option>
          </select>

          <select
            value={filters.status}
            onChange={(event) =>
              updateFilter("status", event.target.value)
            }
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
          >
            <option value="ALL">All Status</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILED">Failed</option>
          </select>

          <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(event) =>
                updateFilter("search", event.target.value)
              }
              className="w-full bg-transparent text-sm outline-none"
              placeholder="Search recipient, message or error"
            />
          </div>
        </div>
      </div>

      <DataTable
        loading={loading}
        rows={rows.map((row, index) => ({
          ...row,
          key: row.notificationId ?? index,
        }))}
        empty="No notifications found for the selected filters."
        testId="notification-logs-table"
        columns={[
          {
            key: "sentAt",
            header: "When",
            render: (row) => dateTime(row.sentAt),
          },
          {
            key: "notificationType",
            header: "Type",
          },
          {
            key: "channel",
            header: "Channel",
          },
          {
            key: "recipientValue",
            header: "Recipient",
          },
          {
            key: "status",
            header: "Status",
            render: (row) => <StatusBadge value={row.status} />,
          },
          {
            key: "message",
            header: "Message",
            render: (row) => (
              <span className="line-clamp-2 max-w-md text-slate-600">
                {row.message || "—"}
              </span>
            ),
          },
          {
            key: "errorMessage",
            header: "Error",
            render: (row) => row.errorMessage || "—",
          },
        ]}
      />
    </div>
  );
};
